import {
  useMutation,
  useQueryClient,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
} from '@tanstack/react-query'
import { InferenceAPI, useTokenAndOrgId } from './utils'
import {
  getStorageViewSort,
  StorageViewSortKeys,
} from '@/pages/DashboardStorage/types'
import { processPaginatedData } from './utils'
import { toast } from 'react-hot-toast'
import {
  CreateStorageVolumeArgs,
  ListStorageVolumeDataItem,
  StorageVolumeStatus,
  GoogleDriveInitData,
  AddGoogleDriveArgs,
  DeleteGoogleDriveArgs,
} from './inference.gen'
import { useState, useEffect } from 'react'

// Constants
const STORAGE_REFRESH_INTERVAL = 2000 // 2 seconds

export const useListStorageVolumes = ({
  status,
  showAllUsers = false,
  searchTerm = '',
  sort = undefined,
  autoRefreshEnabled = true,
  showPublic = false,
}: {
  status?: StorageVolumeStatus
  showAllUsers?: boolean
  searchTerm?: string
  sort?: StorageViewSortKeys
  autoRefreshEnabled?: boolean
  showPublic?: boolean
}) => {
  const { orgId, token } = useTokenAndOrgId()
  const [refetchInterval, setRefetchInterval] = useState<number | false>(false)
  const queryClient = useQueryClient()

  const sortData = sort
    ? getStorageViewSort(sort)
    : getStorageViewSort(StorageViewSortKeys.CREATED_AT_DESC)

  const fetchStorageVolumes = async ({ pageParam = 1 }) => {
    try {
      const result = await InferenceAPI.listStorageVolumes({
        page: {
          page_number: pageParam,
          page_size: 5,
          status: status || '',
          show_all_users: showAllUsers,
          search_text: searchTerm,
          sort_by: sortData.sortBy,
          sort_direction: sortData.sortDirection,
          show_public: showPublic,
        },
      })

      return {
        instances: result.volumes,
        total: result.total,
      }
    } catch (error) {
      console.error('Error fetching storage volumes:', error)
      return {
        instances: [],
        total: 0,
      }
    }
  }

  // Create the query with a static refetchInterval
  const queryResult: UseInfiniteQueryResult<{
    allVolumes: ListStorageVolumeDataItem[]
    total: number
  }> = useInfiniteQuery({
    queryKey: [
      'list-storage-volumes',
      orgId,
      token,
      status,
      showAllUsers,
      sort,
      searchTerm,
      showPublic,
    ],
    queryFn: fetchStorageVolumes,
    enabled: !!orgId && !!token,
    getNextPageParam: (lastPage, pages) => {
      return lastPage.instances.length === 5 ? pages.length + 1 : undefined
    },
    select: (data) => {
      const processedData = processPaginatedData(data.pages)
      return {
        allVolumes: processedData.allInstances,
        total: processedData.total,
      }
    },
    initialPageParam: 1,
    refetchInterval,
  })

  // Set up auto-refresh
  useEffect(() => {
    // Simple: always refresh every 2 seconds when autoRefreshEnabled is true
    setRefetchInterval(autoRefreshEnabled ? STORAGE_REFRESH_INTERVAL : false)
  }, [autoRefreshEnabled])

  return queryResult
}

export const useListStorageVolumesPaginated = ({
  page = 1,
  pageSize = 10,
  searchTerm = '',
  showAllUsers = false,
  showPublic = false,
}: {
  page?: number
  pageSize?: number
  searchTerm?: string
  showAllUsers?: boolean
  showPublic?: boolean
}) => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery({
    queryKey: [
      'list-storage-volumes-paginated',
      orgId,
      token,
      page,
      pageSize,
      searchTerm,
      showAllUsers,
      showPublic,
    ],
    queryFn: async () => {
      try {
        const result = await InferenceAPI.listStorageVolumes({
          page: {
            page_number: page,
            page_size: pageSize,
            status: '',
            show_all_users: showAllUsers,
            search_text: searchTerm,
            sort_by: 'created_at',
            sort_direction: 'desc',
            show_public: showPublic,
          },
        })

        // Filter out deleted volumes
        const filteredVolumes = result.volumes.filter(
          (volume) => !volume.storage_volume.is_deleted
        )

        return {
          volumes: filteredVolumes,
          total: result.total,
          totalPages: Math.ceil(result.total / pageSize),
        }
      } catch (error) {
        console.error('Error fetching storage volumes:', error)
        return {
          volumes: [],
          total: 0,
          totalPages: 0,
        }
      }
    },
    enabled: !!orgId && !!token,
  })
}

export const useCreateStorageVolume = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateStorageVolumeArgs) => {
      const result = await InferenceAPI.createStorageVolume({
        name: data.name,
        size_gb: data.size_gb,
        read_only: data.read_only,
        google_drive_metadata: data.google_drive_metadata,
        is_public: data.is_public,
        type_id: data.type_id,
      })

      queryClient.invalidateQueries({
        queryKey: ['list-storage-volumes', orgId, token],
        exact: false,
      })

      queryClient.invalidateQueries({
        queryKey: ['list-google-drives', orgId, token],
        exact: false,
      })

      return result.ok
    },
  })
}

export const useDeleteStorageVolume = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (volumeId: string) => {
      const result = await InferenceAPI.deleteStorageVolume({
        volume_id: Number(volumeId),
      })

      queryClient.invalidateQueries({
        queryKey: ['list-storage-volumes', orgId, token],
        exact: false,
      })
      queryClient.invalidateQueries({
        queryKey: ['list-google-drives', orgId, token],
        exact: false,
      })

      toast.success('Storage volume deleted successfully')

      return result.ok
    },
  })
}

export const useUpdateStorageVolume = () => {
  const { orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: {
      volume_id: number
      name: string
      is_public: boolean
      google_drive_metadata?: GoogleDriveInitData[]
    }) => {
      const result = await InferenceAPI.updateStorageVolume({
        volume_id: data.volume_id,
        data: {
          name: data.name,
          is_public: data.is_public,
          google_drive_metadata: data.google_drive_metadata || [],
        },
      })

      queryClient.invalidateQueries({
        queryKey: ['list-storage-volumes', orgId],
        exact: false,
      })

      return result.ok
    },
  })
}

export const getTokenMessage = (token: string | undefined) => {
  if (!token) {
    console.error('No token provided')
    return '[INVALID TOKEN, CLICK REGENERATE TOKEN or CONTACT SUPPORT]'
  }
  return `inference_ai_token=${token}`
}

export const useListGoogleDriveTokens = () => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['list-google-drive-tokens', orgId, token],
    queryFn: async () => {
      const result = await InferenceAPI.listGoogleDriveTokens({
        page_size: 100,
        page_number: 1,
      })
      return result
    },
  })
}

export const useCreateGoogleDriveToken = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (url: string) => {
      const result = await InferenceAPI.createGoogleDriveToken({
        url,
      })
      queryClient.invalidateQueries({
        queryKey: ['list-google-drive-tokens', orgId, token],
        exact: false,
      })
      return result.token
    },
  })
}

export const useDeleteGoogleDriveToken = () => {
  const { token, orgId } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await InferenceAPI.deleteGoogleDriveToken({
        id,
      })
      return result.ok
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['list-google-drives', orgId, token],
        exact: false,
      })
    },
  })
}

export const useListGoogleDrives = () => {
  const { orgId, token } = useTokenAndOrgId()

  return useQuery({
    queryKey: ['list-google-drives', orgId, token],
    queryFn: async () => {
      const result = await InferenceAPI.listGoogleDrive({
        page_size: 100,
        page_number: 1,
      })
      return result
    },
  })
}

export const useAddGoogleDrive = () => {
  const { orgId, token } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: AddGoogleDriveArgs) => {
      const result = await InferenceAPI.addGoogleDrive(data)

      queryClient.invalidateQueries({
        queryKey: ['list-google-drives', orgId, token],
        exact: false,
      })
      return result.item
    },
  })
}

export const useDeleteGoogleDrive = () => {
  const { orgId, token } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: DeleteGoogleDriveArgs) => {
      const result = await InferenceAPI.deleteGoogleDrive(data)

      queryClient.invalidateQueries({
        queryKey: ['list-google-drives', orgId, token],
        exact: false,
      })
      return result.ok
    },
  })
}

export const useGoogleDriveTokenCheck = () => {
  const { data: tokens } = useListGoogleDriveTokens()

  return useMutation({
    mutationFn: async (url: string) => {
      const existingToken = tokens?.items.find(
        (token) => token.url.trim() === url.trim()
      )

      return {
        hasExistingToken: !!existingToken,
        existingToken,
      }
    },
  })
}

export const useSyncStorageGoogleDrive = () => {
  const { orgId, token } = useTokenAndOrgId()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (volumeId: number) => {
      const result = await InferenceAPI.syncStorageGoogleDrive({
        volume_id: volumeId,
      })

      // Invalidate storage volumes to trigger refetch and show sync progress
      queryClient.invalidateQueries({
        queryKey: ['list-storage-volumes', orgId, token],
        exact: false,
      })

      return result.ok
    },
    onSuccess: () => {
      toast.success('Google Drive sync initiated successfully')
    },
    onError: (error) => {
      toast.error(`Failed to start sync: ${error.message}`)
    },
  })
}
